import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Calendar, User, Share2, MessageSquare, Star, Send } from 'lucide-react';
import { motion } from 'framer-motion';
import { getBlogById, rateBlog, getCommentsForBlog, addComment } from '../services/api';
import AdBanner from '../components/AdBanner';
import SEO from '../components/SEO';
import { useAuth } from '../context/AuthContext';

const BlogDetails = () => {
  const { id } = useParams();
  const { user } = useAuth();
  
  const [blog, setBlog] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [loading, setLoading] = useState(true);
  
  const [userRating, setUserRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [hasRated, setHasRated] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      const bData = await getBlogById(id);
      const cData = await getCommentsForBlog(id);
      setBlog(bData);
      setComments(cData);
      setLoading(false);
    };
    fetchData();
  }, [id]);

  const handleRate = async (rateVal) => {
    if (hasRated) return;
    setUserRating(rateVal);
    setHasRated(true);
    await rateBlog(blog.id, rateVal);
    // Optimistic update
    setBlog(prev => ({ 
      ...prev, 
      ratingCount: prev.ratingCount + 1,
      rating: ((prev.rating * prev.ratingCount) + rateVal) / (prev.ratingCount + 1)
    }));
  };

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    
    const authorName = user ? user.name : "Guest";
    const res = await addComment(blog.id, newComment, authorName);
    if (res.success) {
      setComments([...comments, res.comment]);
      setNewComment("");
    }
  };

  const shareUrl = window.location.href;
  const shareText = blog ? blog.title : "Check out this blog!";

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-saffron-600"></div>
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-stone-50 text-center px-4">
        <h2 className="text-3xl font-bold text-stone-800 mb-4">Blog Not Found</h2>
        <Link to="/blog" className="px-6 py-3 bg-saffron-600 text-white rounded-lg hover:bg-saffron-700 transition">Back to Blogs</Link>
      </div>
    );
  }

  return (
    <div className="bg-white min-h-screen pb-24">
      <SEO 
        title={blog.title} 
        description={blog.excerpt || (blog.content ? blog.content.replace(/<[^>]+>/g, '').substring(0, 160) : 'Read this insightful blog post on Nathnagri Temples.')} 
        image={blog.image}
      />
      {/* Article Header */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-12">
        <div className="mb-8">
          <AdBanner placement="banner" pageName="blog" />
        </div>

        <Link to="/blog" className="inline-flex items-center text-stone-500 hover:text-saffron-600 transition-colors mb-8 font-medium">
          <ArrowLeft className="w-4 h-4 mr-2" /> सभी लेख (All Blogs)
        </Link>

        <motion.h1 initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="text-3xl md:text-5xl font-extrabold text-stone-900 leading-tight mb-6">
          {blog.title}
        </motion.h1>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }} className="flex flex-col sm:flex-row sm:items-center justify-between border-y border-stone-100 py-4 mb-8 space-y-4 sm:space-y-0">
          <div className="flex items-center space-x-6">
            <div className="flex items-center space-x-2 text-stone-600 text-sm">
              <User className="w-5 h-5 text-saffron-500" />
              <span className="font-medium">{blog.author}</span>
            </div>
            <div className="flex items-center space-x-2 text-stone-600 text-sm">
              <Calendar className="w-5 h-5 text-saffron-500" />
              <span>{blog.date}</span>
            </div>
            <div className="flex items-center space-x-1 text-yellow-500 bg-yellow-50 px-2 rounded font-bold text-sm">
              <Star className="w-4 h-4 fill-yellow-500" />
              <span>{typeof blog.rating === 'number' ? blog.rating.toFixed(1) : blog.rating} ({blog.ratingCount} Ratings)</span>
            </div>
          </div>
          
          <div className="flex items-center space-x-3 text-sm">
            <span className="text-stone-500 flex items-center font-medium"><Share2 className="w-4 h-4 mr-1"/> Share:</span>
            <a href={`https://wa.me/?text=${encodeURIComponent(shareText + " " + shareUrl)}`} target="_blank" rel="noreferrer" className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center text-green-600 hover:bg-green-600 hover:text-white transition-colors">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 00-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
            </a>
            <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noreferrer" className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 hover:bg-blue-600 hover:text-white transition-colors">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.469h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.469h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
            </a>
            <a href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noreferrer" className="w-8 h-8 rounded-full bg-sky-100 flex items-center justify-center text-sky-500 hover:bg-sky-500 hover:text-white transition-colors">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/></svg>
            </a>
          </div>
        </motion.div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12 flex flex-col lg:flex-row gap-8">
        <div className="lg:w-[70%]">
          {/* Featured Image */}
          <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.5, delay: 0.2 }} className="rounded-3xl overflow-hidden aspect-video shadow-xl shadow-stone-200/50 mb-10">
            <img src={blog.image} alt={blog.title} className="w-full h-full object-cover" />
          </motion.div>

          {/* Article Body */}
          <div className="prose prose-stone prose-lg max-w-none text-stone-700 leading-relaxed md:text-xl">
            {/* If content has HTML tags (because of rich text), render it safely */}
            <div 
              dangerouslySetInnerHTML={{ __html: blog.content }} 
              className="[&>p]:mb-6 [&>div]:mb-6 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-6 [&_h1]:text-3xl [&_h1]:font-bold [&_h1]:mb-4 [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:mb-4"
            />
            
            {/* Embedded Ad inside content body */}
            <div className="my-8">
               <AdBanner placement="in-content" pageName="blog" />
            </div>
          </div>

          {/* User Ratings */}
          <div className="mt-12 bg-white border-2 border-yellow-100 rounded-3xl p-8 flex flex-col md:flex-row items-center justify-between">
            <div className="mb-4 md:mb-0 text-center md:text-left">
              <h3 className="text-xl font-bold text-stone-800">Enjoyed this article?</h3>
              <p className="text-stone-500 text-sm">Please rate it out of 5 stars.</p>
            </div>
            <div className="flex items-center space-x-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  onClick={() => handleRate(star)}
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                  className="focus:outline-none transition-transform hover:scale-110"
                >
                  <Star className={`w-10 h-10 ${(hoverRating || userRating) >= star ? 'text-yellow-400 fill-yellow-400' : 'text-stone-200'} transition-colors duration-200`} />
                </button>
              ))}
            </div>
          </div>

          {/* Comments Section */}
          <div className="mt-16">
            <h3 className="text-2xl font-bold text-stone-900 mb-8 border-b border-stone-200 pb-4">
              Comments ({comments.length})
            </h3>
            
            {/* Add Comment */}
            <div className="bg-stone-50 rounded-2xl p-6 border border-stone-200 mb-8">
              <form onSubmit={handleCommentSubmit}>
                <textarea
                  className="w-full bg-white text-stone-800 border border-stone-200 rounded-xl p-4 focus:ring-2 focus:ring-saffron-500 resize-none shadow-sm"
                  rows="3"
                  placeholder="Share your thoughts..."
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                ></textarea>
                <div className="mt-4 flex justify-between items-center">
                  <span className="text-sm text-stone-500 font-medium">Commenting as: {user ? user.name : "Guest"}</span>
                  <button type="submit" className="inline-flex items-center px-6 py-2.5 bg-stone-800 text-white font-medium rounded-lg hover:bg-black transition shadow-sm">
                    <Send className="w-4 h-4 mr-2" /> Submit
                  </button>
                </div>
              </form>
            </div>

            {/* Render Comments List */}
            <div className="space-y-6">
              {comments.map((c) => (
                <div key={c.id} className="flex space-x-4">
                  <div className="w-10 h-10 bg-gradient-to-br from-stone-400 to-stone-600 rounded-full flex items-center justify-center text-white font-bold shrink-0 shadow-sm">
                    {c.author.charAt(0)}
                  </div>
                  <div className="bg-white rounded-2xl p-5 flex-1 border border-stone-100 shadow-sm">
                    <div className="flex justify-between items-start mb-2">
                      <h4 className="font-bold text-stone-800">{c.author}</h4>
                      <span className="text-xs text-stone-400">{c.date}</span>
                    </div>
                    <p className="text-stone-600 text-sm leading-relaxed">{c.text}</p>
                  </div>
                </div>
              ))}
              {comments.length === 0 && <p className="text-center text-stone-500 italic py-8">Be the first to comment on this article!</p>}
            </div>
          </div>

        </div>

        {/* Sticky Sidebar */}
        <div className="lg:w-[30%]">
          <div className="sticky top-28 space-y-8">
            <AdBanner placement="sidebar" pageName="blog" />
            
            <div className="bg-stone-50 rounded-2xl p-6 border border-stone-200">
              <h3 className="font-bold text-stone-900 mb-4 flex items-center"><MessageSquare className="w-5 h-5 mr-2 text-saffron-600" /> Need Help?</h3>
              <p className="text-sm text-stone-600 mb-4">If you find any inappropriate content in comments or articles, please report it to our moderation team.</p>
              <button className="w-full py-2 bg-white text-stone-700 font-medium border border-stone-300 rounded-xl hover:bg-stone-100 transition">Report Issue</button>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default BlogDetails;
